<template>
    <!-- the box score of one team, the totals at the bottom -->
    <div class="box-score">
        <section-heading title="Box score" icon="mdi-table-large" />
        <div class="box-score__teams" role="tablist">
            <button
                v-for="(team, index) in teams"
                :key="index"
                type="button"
                role="tab"
                class="box-score__team"
                :class="{ 'box-score__team--active': option === index }"
                :aria-selected="option === index"
                @click="option = index"
            >
                <span class="cl-logo box-score__team-logo"><img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt=""></span>
                <span class="box-score__team-name">{{ team.name }}</span>
            </button>
        </div>
        <v-data-table
            :headers="headers"
            :items="tableItems"
            class="cl-table box-score__table"
            dark
            hide-default-footer
            :mobile-breakpoint="0"
            :items-per-page="-1"
            item-key="id"
            no-data-text="Δεν υπάρχουν ακόμη στατιστικά"
            @click:row="item => $router.push({ path: `/Player/${item.id}` })"
        >
            <template v-slot:item.player="{ item }">
                <span class="box-score__who">
                    <span class="box-score__jersey">{{ item.jersey ?? '–' }}</span>
                    <span class="box-score__name">{{ item.player }}</span>
                </span>
            </template>
            <template v-slot:item.playTimeInSecs="{ item }">
                <span class="box-score__muted">{{ minutes(item.playTimeInSecs) }}</span>
            </template>
            <template v-slot:item.points="{ item }">
                <span class="box-score__pts">{{ item.points }}</span>
            </template>
            <template v-slot:item.two_points_percentage="{ item }">
                <shot-cell :made="item.twoMade" :att="item.twoAtt" />
            </template>
            <template v-slot:item.three_points_percentage="{ item }">
                <shot-cell :made="item.threeMade" :att="item.threeAtt" />
            </template>
            <template v-slot:item.free_throw_percentage="{ item }">
                <shot-cell :made="item.ftMade" :att="item.ftAtt" />
            </template>
            <template v-slot:item.pir="{ item }">
                <span class="box-score__eff">{{ item.pir }}</span>
            </template>
            <template v-if="tableItems.length" v-slot:body.append>
                <tr class="box-score__totals">
                    <td class="text-start">Σύνολο</td>
                    <td />
                    <td>{{ totals.points }}</td>
                    <td><shot-cell :made="totals.twoMade" :att="totals.twoAtt" /></td>
                    <td><shot-cell :made="totals.threeMade" :att="totals.threeAtt" /></td>
                    <td><shot-cell :made="totals.ftMade" :att="totals.ftAtt" /></td>
                    <td>{{ totals.offensiveRebounds }}</td>
                    <td>{{ totals.defensiveRebounds }}</td>
                    <td>{{ totals.rebounds }}</td>
                    <td>{{ totals.assists }}</td>
                    <td>{{ totals.steals }}</td>
                    <td>{{ totals.turnovers }}</td>
                    <td>{{ totals.blockedShots }}</td>
                    <td>{{ totals.fouls }}</td>
                    <td>{{ totals.pir }}</td>
                </tr>
            </template>
        </v-data-table>
    </div>
</template>

<script>
    import SectionHeading from '@/components/SectionHeading'
    import { foulsOf } from '@/utils/teamStats'

    // made / attempted, with the percentage under it
    const ShotCell = {
        functional: true,
        props: { made: Number, att: Number },
        render(h, { props }) {
            const pct = props.att ? Math.round(props.made / props.att * 100) : 0
            return h('span', { class: 'box-score__shot' }, [
                h('span', `${props.made}/${props.att}`),
                h('small', props.att ? `${pct}%` : '–')
            ])
        }
    }

    const SUMMED = ['points', 'offensiveRebounds', 'defensiveRebounds', 'rebounds', 'assists', 'steals', 'turnovers', 'blockedShots', 'fouls', 'pir',
                    'twoMade', 'twoAtt', 'threeMade', 'threeAtt', 'ftMade', 'ftAtt']

    export default {
        components: { SectionHeading, ShotCell },
        props: {
            match: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                option: 0,
                assetsUrl: 'https://storage.googleapis.com',
                headers: [
                    { text: 'Παίκτης', value: 'player', align: 'start' },
                    { text: 'MIN', value: 'playTimeInSecs', align: 'center' },
                    { text: 'PTS', value: 'points', align: 'center' },
                    { text: '2P', value: 'two_points_percentage', align: 'center' },
                    { text: '3P', value: 'three_points_percentage', align: 'center' },
                    { text: 'FT', value: 'free_throw_percentage', align: 'center' },
                    { text: 'OR', value: 'offensiveRebounds', align: 'center' },
                    { text: 'DR', value: 'defensiveRebounds', align: 'center' },
                    { text: 'TR', value: 'rebounds', align: 'center' },
                    { text: 'AST', value: 'assists', align: 'center' },
                    { text: 'STL', value: 'steals', align: 'center' },
                    { text: 'TO', value: 'turnovers', align: 'center' },
                    { text: 'BLK', value: 'blockedShots', align: 'center' },
                    { text: 'FC', value: 'fouls', align: 'center' },
                    { text: 'EFF', value: 'pir', align: 'center' }
                ]
            }
        },
        computed: {
            teams() {
                return [this.match.team1 || {}, this.match.team2 || {}]
            },
            tableItems() {
                const team = this.option ? this.match.team2 : this.match.team1
                const players = ((team && team.players) || [])
                    .filter(player => player.active && this.played(player))
                    .sort((a, b) => (a.jersey ?? 999) - (b.jersey ?? 999))
                return players.map(p => {
                    const s = (p.stats && p.stats[0]) || {}
                    const twoMade = Number(s.twoPointsMade) || 0
                    const twoAtt = Number(s.twoPointAttempts) || 0
                    const threeMade = Number(s.threePointsMade) || 0
                    const threeAtt = Number(s.threePointAttempts) || 0
                    const ftMade = Number(s.freeThrowsMade) || 0
                    const ftAtt = Number(s.freeThrowAttempts) || 0
                    return {
                        ...p,
                        player: `${p.name || ''} ${p.surname || ''}`.trim(),
                        points: Number(s.points) || 0,
                        two_points_percentage: twoAtt ? (twoMade / twoAtt) * 100 : 0,
                        three_points_percentage: threeAtt ? (threeMade / threeAtt) * 100 : 0,
                        free_throw_percentage: ftAtt ? (ftMade / ftAtt) * 100 : 0,
                        offensiveRebounds: Number(s.offensiveRebounds) || 0,
                        defensiveRebounds: Number(s.defensiveRebounds) || 0,
                        rebounds: Number(s.rebounds) || 0,
                        assists: Number(s.assists) || 0,
                        steals: Number(s.steals) || 0,
                        turnovers: Number(s.turnovers) || 0,
                        blockedShots: Number(s.blockedShots) || 0,
                        personalFouls: Number(s.personalFouls) || 0,
                        // every foul: personal, technical and unsportsmanlike
                        fouls: foulsOf(s),
                        pir: Number(s.pir) || 0,
                        playTimeInSecs: Number(s.playTimeInSecs) || 0,
                        twoMade,
                        twoAtt,
                        threeMade,
                        threeAtt,
                        ftMade,
                        ftAtt
                    }
                })
            },
            totals() {
                const sum = {}
                SUMMED.forEach(key => {
                    sum[key] = this.tableItems.reduce((total, item) => total + (item[key] || 0), 0)
                })
                return sum
            }
        },
        methods: {
            // in the box score only the players who took part: minutes on the clock or anything on the sheet
            played(player) {
                const line = player.stats && player.stats[0]
                if (!line) return false
                if ((Number(line.playTimeInSecs) || 0) > 0) return true
                return ['points', 'twoPointAttempts', 'threePointAttempts', 'freeThrowAttempts', 'rebounds', 'assists', 'steals',
                        'turnovers', 'blockedShots'].some(key => (Number(line[key]) || 0) > 0) || foulsOf(line) > 0
            },
            minutes(secs) {
                if (!secs || secs < 0) return '–'
                const m = Math.floor(secs / 60)
                const s = Math.floor(secs % 60)
                return `${m}:${s < 10 ? '0' : ''}${s}`
            }
        }
    }
</script>

<style scoped>
.box-score__teams {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    margin-bottom: 12px;
    padding: 4px;
    border-radius: 12px;
    background: #232323;
}

.box-score__team {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 0;
    padding: 8px 12px;
    border-radius: 9px;
    color: rgba(255, 255, 255, 0.65);
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    transition: background 0.15s, color 0.15s;
}

.box-score__team:hover {
    color: #fff;
}

.box-score__team--active {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #fff;
}

.box-score__team-logo {
    width: 28px;
    height: 28px;
    border-radius: 6px;
}

.box-score__team-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

/* the player stays in view while the figures scroll */
.box-score__table >>> table > thead > tr > th:first-child,
.box-score__table >>> table > tbody > tr > td:first-child {
    position: sticky;
    left: 0;
    z-index: 2;
    padding-left: 14px !important;
    background: #232323;
}

.box-score__table >>> table > thead > tr > th:first-child {
    z-index: 3;
    background: #1b1b1b !important;
}

.box-score__table >>> table > tbody > tr.box-score__totals > td:first-child {
    background: #1b1b1b;
}

.box-score__table >>> table > thead > tr > th,
.box-score__table >>> table > tbody > tr > td {
    padding: 0 6px !important;
    text-align: center;
}

.box-score__table >>> table > tbody > tr > td {
    height: 46px !important;
}

.box-score__table >>> tbody > tr:not(.box-score__totals) {
    cursor: pointer;
}

.box-score__who {
    display: flex;
    align-items: center;
    gap: 10px;
}

.box-score__jersey {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    min-width: 26px;
    height: 26px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
    font-size: 0.78rem;
    font-weight: 700;
}

.box-score__name {
    font-weight: 600;
    white-space: nowrap;
}

.box-score__muted {
    color: rgba(255, 255, 255, 0.6);
}

.box-score__pts {
    font-weight: 800;
    color: #fff;
}

.box-score__eff {
    font-weight: 800;
    color: #03e5b7;
}

.box-score__table >>> .box-score__shot {
    display: inline-flex;
    flex-direction: column;
    line-height: 1.15;
}

.box-score__table >>> .box-score__shot small {
    font-size: 0.68rem;
    color: rgba(255, 255, 255, 0.45);
}

.box-score__totals td {
    background: #1b1b1b;
    border-top: 2px solid rgba(3, 229, 183, 0.35);
    font-weight: 800;
}

.box-score__totals:hover {
    background: transparent !important;
}

@media (max-width: 700px) {
    .box-score__table >>> table > tbody > tr > td {
        font-size: 0.8rem !important;
    }

    .box-score__team {
        gap: 6px;
        padding: 7px 8px;
        font-size: 0.78rem;
    }

    .box-score__team-logo {
        width: 22px;
        height: 22px;
    }

    .box-score__name {
        max-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
</style>
